import React, {
  Suspense,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import { isIE, browserName } from 'react-device-detect';
import BrowserAlertModal from '@components/modal/BrowserAlertModal';
import mainSetting from '@app/mainSetting';
import { fuego, useDocument, useCollection } from '@nandorojo/swr-firestore';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import LayoutWrapper from './layouts/LayoutWrapper';
import {
  gatePageActiveState,
  iconDirectionState,
  layoutDirState,
  layoutTypeInitialState,
  layoutTypeState,
  layoutWidthState,
  logoTypeState,
  menuFixedState,
  menuHeightState,
  menuTypeState,
  menuWidthState,
  particleEffectOptionsState,
  showBlogSlideState,
  showSideState,
  topNavSideSeparatedState,
} from '@atom/setting/homepageSet';
import {
  homeSettingState,
  homeSettingLoadingState,
  mainHomeSettingState,
  defaultCurrencyState,
  bgEffSetState,
  standingCharacterState,
} from '@atom/setting/homeSettingState';
import { representativePhotocardRefState } from '@atom/setting/homeSettingState';
import {
  boardSetListState,
  boardSetListLoadingState,
} from '@atom/setting/boardSetListState';
import AlertModal from '@components/modal/AlertModal';
import {
  CollectionPath,
  ImainhomeDoc,
  SettingCollectionPath,
} from '@typings/collections.enum';
import MusicPlayerDraggable from '@template/MusicPlayer/MusicPlayerDraggable';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';
import { masterSet } from '@masterSetting/masterSet/masterSetting';
import { cookieStore } from '@store/cookie/cookieStore';
import { getPathKey } from '@utils/getPath';
import BackgroundEffect from './options/BackgroundEffect';
import { registerOpenState } from '@atom/modal/modal';
import RegisterModal from '@pages/Register/RegisterModal';
import GatePage from '@pages/Gate';
import { IgatePageSet } from '@typings/pages/gatePage.interface';
import NotificationDraggable from '@template/Notifications/NotificationDraggable';
import { useGlobalClickSound } from '@hooks/useGlobalClickSound';
import MouseEffect from './options/MouseEffect';
import ClickEffectsManager from '@components/effect/ClickEffects/ClickEffectsManager';
import { IFontManagerSetting } from '@typings/setting/fontManager';
import { useCustomFonts } from '@hooks/useCustomFonts';

import initialSet from './initialSet';
import {
  addBoardSet,
  setInitialBoard,
} from '@store/firebase/setting/set-board';
import { resetHashtagDocIdList } from '@store/firebase/hashtag/reset-hashtagIdList';
import { resetIdList } from '@store/firebase/timeline/reset-timeline';
import { BOARD_TYPE } from '@typings/boardType';
import {
  rebaseAlbum,
  rebaseBoardsTheme,
} from '@store/firebase/setting/reset-themeSet';
import { LAYOUT_DIR } from '@typings/setting/layoutDir';
import NeighborhoodBtn from '@template/Neighbor/NeighborhoodBtn';
import { ImainHome } from '@typings/setting/mainHome.interface';
import Simeji from '@template/Simeji/Simeji';
import {
  moveAllDoc,
  refetchAllDoc,
  refetchDocNumberID,
  refetchSeries,
  resetAllEmoticonOrder,
  resetDocIdList,
} from '@store/firebase/hashtag/reset-docIdList';
import { migratePageSetting } from '@store/firebase/migratePageSetting';
import { setInitialBackgroundEffect } from '@store/firebase/setting/set-backgroundEffect';
import { setInitialMainHome } from '@store/firebase/mainhome/set-home';
import { setInitialHomepage } from '@store/firebase/setting/set-homepage';
import ExtraRoute from '@routes/ExtraRoute';
import { Ijukebox } from '@typings/setting/jukebox.interface';
import { NotificationPermissionManager } from '@components/system/NotificationPermissionManager';
import { MENU_DESIGN_TYPE } from '@typings/menu/menuType';
import { formatPixelValue } from '@utils/formatPixelValue';
import CharacterPanel from '@template/CharacterPanel/CharacterPanel';
import { charPanelLayoutOverrideState } from '@atom/setting/charPanelOverride';
import ChatMessageNotification from '@template/Notifications/ChatMessageNotification';
import { useNotificationSync } from '@hooks/useNotificationSync';
import ReplyModal from '@pages/Threads/components/ReplyModal';

// 개발 환경 알림 설정 검증 도구 (window.__notiTest 등록)
if (process.env.NODE_ENV === 'development') {
  import('@utils/dev/notificationTestUtils');
}
import PetDraggable from '@pages/Pets/PetView/PetDraggable';
import { IBoardSet } from '@typings/menu/boardSetType';
import showNav from '@utils/showNav';
import {
  IPhotoCard,
  IPhotocardAutoSet,
} from '@typings/photocard/photocard.interface';
import { FormResponse, FormConfig } from '@typings/form/form.interface';
import { User } from '@typings/setting/userType.interface';
import {
  photocardAutoSetState,
  mainPhotocardDataState,
  representativeFormDataState,
  IRepresentativeForm,
} from '@atom/setting/photocardAutoSetState';
import { ICampItem } from '@typings/photocard/camp.interface';
import { IstoreCommon } from '@typings/store/storeCommon.interface';
import { IbackgroundEffect } from '@typings/setting/backgroundEffect.interface';
import FloatingPet from '@pages/Pets/components/FloatingPet/FloatingPet';
import {
  petActivePetIDState,
  petFloatingActiveState,
} from '@atom/setting/petFloating';
import { UserPet } from '@typings/pets/pet.interface';
import WebglBackground from '@template/WebglBackground/WebglBackground';
import { setDynamicManifest } from '@utils/dynamicManifest';
import {
  timelineCommentSettingState,
  timelineCommentSettingLoadedState,
} from '@atom/setting/timelineCommentSetting';
import { timelineResultDesignState } from '@atom/setting/timelineResultDesign';
import { playerOpenState, notificationOpenState } from '@atom/setting/jukebox';
import { chatFloatingOpenState } from '@atom/chat/chatFloating';
import { currentUserDataState, currentUserIDState } from '@atom/setting/user';
import {
  ITimelineCommentSetting,
  defaultTimelineCommentSetting,
  ITimelineResultDesignSetting,
  defaultTimelineResultDesignSetting,
} from '@typings/setting/timelineSetting.interface';

const ChatFloating = React.lazy(() => import('@pages/Chat/ChatFloating'));
const VersionUpdateModal = React.lazy(
  () => import('@components/navigation/Footer/VersionUpdateModal'),
);
const BackgroundDecorations = React.lazy(
  () => import('@template/BackgroundDecorations/BackgroundDecorations'),
);
const GlobalStickerDisplay = React.lazy(
  () => import('@components/display/GlobalStickerDisplay'),
);
const RaidNotificationSnackbar = React.lazy(
  () => import('@components/notification/RaidNotificationSnackbar'),
);
const GlobalRaidModal = React.lazy(() => import('@pages/Raid/GlobalRaidModal'));
const PWAInstallBanner = React.lazy(
  () => import('@components/system/PWAInstallBanner'),
);
const AnnouncementPopup = React.lazy(
  () => import('@template/AnnouncementPopup/AnnouncementPopup'),
);

function Main() {
  type FloatingLayerKey = 'chat' | 'musicPlayer' | 'notification';

  // 알림 설정 Firestore 동기화 (로그인/로그아웃 감지)
  useNotificationSync();

  // settings
  const setLayoutType = useSetRecoilState(layoutTypeState);
  const setInitialLayoutType = useSetRecoilState(layoutTypeInitialState);
  const setLayoutDir = useSetRecoilState(layoutDirState);
  const setLogoType = useSetRecoilState(logoTypeState);
  const setMenuTypeAtom = useSetRecoilState(menuTypeState);
  const setMenuFixed = useSetRecoilState(menuFixedState);
  const setMenuHeight = useSetRecoilState(menuHeightState);
  const setMenuWidth = useSetRecoilState(menuWidthState);
  const setLayoutWidth = useSetRecoilState(layoutWidthState);
  const setShowAside = useSetRecoilState(showSideState);
  const setShowBlogSlide = useSetRecoilState(showBlogSlideState);
  const setIconDirection = useSetRecoilState(iconDirectionState);
  const setTopNavSideSeparated = useSetRecoilState(topNavSideSeparatedState);
  const [gatePageActive, setGatePageActive] =
    useRecoilState(gatePageActiveState);

  // 📍 중앙화된 홈페이지 설정 관리
  const setHomeSetting = useSetRecoilState(homeSettingState);
  const setBgEffSet = useSetRecoilState(bgEffSetState);
  const setParticleEffectOptions = useSetRecoilState(particleEffectOptionsState);
  const setHomeSettingLoading = useSetRecoilState(homeSettingLoadingState);
  const setMainHomeSetting = useSetRecoilState(mainHomeSettingState);
  const [repRef, setRepRef] = useRecoilState(representativePhotocardRefState);

  // 📍 중앙화된 보드셋 리스트 관리
  const setBoardSetList = useSetRecoilState(boardSetListState);
  const setBoardSetListLoading = useSetRecoilState(boardSetListLoadingState);

  // 📍 포토카드 기본 설정 관리
  const setPhotocardAutoSet = useSetRecoilState(photocardAutoSetState);
  const setSelectChar = useSetRecoilState(standingCharacterState);
  // 대표 캐릭터 관리 (포토카드)
  const setMainPhotocardData = useSetRecoilState(mainPhotocardDataState);
  // 대표 폼 관리
  const setRepresentativeFormData = useSetRecoilState(
    representativeFormDataState,
  );

  // 📍 타임라인 댓글 옵션 설정 관리 (전역, 한 번만 로드)
  const setTimelineCommentSetting = useSetRecoilState(
    timelineCommentSettingState,
  );
  const setTimelineCommentSettingLoaded = useSetRecoilState(
    timelineCommentSettingLoadedState,
  );

  // 📍 결과 카드 디자인 설정 관리
  const setTimelineResultDesign = useSetRecoilState(timelineResultDesignState);

  const openRegisterModal = useRecoilValue(registerOpenState);
  const currentUserId = useRecoilValue(currentUserIDState);
  const currentUserData = useRecoilValue(currentUserDataState);
  const [isFloating, setIsFloating] = useRecoilState(petFloatingActiveState);

  // 브라우저 모달
  const [openBrowserAlert, setOpenModal] = useState(isIE);
  const closeModal = useCallback(() => setOpenModal(false), []);
  const [loading, setLoading] = useState(false);
  const isMusicPlayerOpen = useRecoilValue(playerOpenState);
  const isNotificationOpen = useRecoilValue(notificationOpenState);
  const isChatOpen = useRecoilValue(chatFloatingOpenState);

  const [floatingLayers, setFloatingLayers] = useState<
    Record<FloatingLayerKey, number>
  >({
    chat: 103,
    musicPlayer: 101,
    notification: 102,
  });
  const floatingLayerRef = useRef(103);
  const prevFloatingOpenRef = useRef<Record<FloatingLayerKey, boolean>>({
    chat: false,
    musicPlayer: false,
    notification: false,
  });

  const bringFloatingToFront = useCallback((target: FloatingLayerKey) => {
    floatingLayerRef.current += 1;
    const nextZIndex = floatingLayerRef.current;

    setFloatingLayers((prev) => ({
      ...prev,
      [target]: nextZIndex,
    }));
  }, []);

  // 음악 컨트롤
  useGlobalClickSound();

  // 브라우저 기본 스크롤 복원 기능 비활성화
  useLayoutEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔧 브라우저 스크롤 복원 기능 비활성화');
      }
    }
  }, []);

  // 📍 중앙화된 홈페이지 설정 로드 (단일 데이터 소스)
  const { data: homeSet, set } = useDocument<IhomepageSetting>(
    SettingCollectionPath.HOMEPAGE,
    { listen: true },
  );

  // 🆕 커스텀 폰트 데이터 로드
  const { data: fontManagerData } = useDocument<IFontManagerSetting>(
    SettingCollectionPath.FONT_MANAGER,
    { listen: true },
  );

  useCustomFonts(fontManagerData?.customFonts);

  // 📍 홈페이지 설정을 Recoil 상태로 동기화
  useEffect(() => {
    if (homeSet) {
      setHomeSetting(homeSet);
      setHomeSettingLoading(false);
      // 대표 포토카드 전역 상태 동기화
      if (homeSet.representativePhotocard !== undefined) {
        setRepRef(homeSet.representativePhotocard || null);
      }

      // 📱 PWA: 동적 매니페스트 생성 (Firestore 설정값 반영)
      setDynamicManifest({
        title: homeSet.homepageSet?.homepageTitle || '',
        description: homeSet.homepageSet?.homepageDescription || '',
        faviconURL: homeSet.homepageSet?.faviconURL || '',
        bgColor: homeSet.background?.backgroundColor || '#ffffff',
        themeColor: homeSet.primary?.primaryColor || '#000000',
      });
    } else {
      setHomeSettingLoading(true);
    }
  }, [homeSet, setHomeSetting, setHomeSettingLoading]);

  // 📍 중앙화된 보드셋 리스트 로드 (단일 데이터 소스)
  const { data: boardSetList } = useCollection<IBoardSet>(
    CollectionPath.BOARD_SET,
    { orderBy: ['order', 'asc'], listen: true },
  );

  // 📍 보드셋 리스트를 Recoil 상태로 동기화
  useEffect(() => {
    if (boardSetList) {
      setBoardSetList(boardSetList);
      setBoardSetListLoading(false);
    } else {
      setBoardSetListLoading(true);
    }
  }, [boardSetList, setBoardSetList, setBoardSetListLoading]);

  // 📍 포토카드 기본 설정 로드 (단일 데이터 소스)
  const { data: photocardAutoSet } = useDocument<IPhotocardAutoSet>(
    `${SettingCollectionPath.PHOTOCARD}`,
    { listen: true },
  );
  useEffect(() => {
    if (photocardAutoSet) {
      setPhotocardAutoSet(photocardAutoSet);
    }
  }, [photocardAutoSet]);

  // 📍 결과 카드 디자인 설정 로드
  const { data: timelineResultDesignData } =
    useDocument<ITimelineResultDesignSetting>(`setting/timeline_resultDesign`, {
      listen: false,
    });
  useEffect(() => {
    if (timelineResultDesignData) {
      setTimelineResultDesign({
        investigationCard: {
          ...defaultTimelineResultDesignSetting.investigationCard,
          ...timelineResultDesignData.investigationCard,
        },
        luckyDrawCard: {
          ...defaultTimelineResultDesignSetting.luckyDrawCard,
          ...timelineResultDesignData.luckyDrawCard,
        },
        giftCard: {
          ...defaultTimelineResultDesignSetting.giftCard,
          ...timelineResultDesignData.giftCard,
        },
      });
    }
  }, [timelineResultDesignData]);

  // 📍 타임라인 댓글 옵션 설정 로드 (앱 초기화 시 한 번만, listen: false)
  const { data: timelineCommentSettingData } =
    useDocument<ITimelineCommentSetting>(
      `${SettingCollectionPath.TIMELINE}_comment`,
      { listen: false },
    );
  useEffect(() => {
    if (timelineCommentSettingData) {
      setTimelineCommentSetting({
        useImage:
          timelineCommentSettingData.useImage ??
          defaultTimelineCommentSetting.useImage,
        useVideo:
          timelineCommentSettingData.useVideo ??
          defaultTimelineCommentSetting.useVideo,
        useEmoticon:
          timelineCommentSettingData.useEmoticon ??
          defaultTimelineCommentSetting.useEmoticon,
        useSecret:
          timelineCommentSettingData.useSecret ??
          defaultTimelineCommentSetting.useSecret,
        useBattle:
          timelineCommentSettingData.useBattle ??
          defaultTimelineCommentSetting.useBattle,
        battleMode:
          timelineCommentSettingData.battleMode ??
          defaultTimelineCommentSetting.battleMode,
        useDice:
          timelineCommentSettingData.useDice ??
          defaultTimelineCommentSetting.useDice,
        useLuckyDraw:
          timelineCommentSettingData.useLuckyDraw ??
          defaultTimelineCommentSetting.useLuckyDraw,
        useCollapseComment:
          timelineCommentSettingData.useCollapseComment ??
          defaultTimelineCommentSetting.useCollapseComment,
        useReply:
          timelineCommentSettingData.useReply ??
          defaultTimelineCommentSetting.useReply,
        showCharacterCount:
          timelineCommentSettingData.showCharacterCount ??
          defaultTimelineCommentSetting.showCharacterCount,
        maxCharacterCount:
          timelineCommentSettingData.maxCharacterCount ??
          defaultTimelineCommentSetting.maxCharacterCount,
        showBoardAuthorBadge:
          timelineCommentSettingData.showBoardAuthorBadge ??
          defaultTimelineCommentSetting.showBoardAuthorBadge,
        boardAuthorBadgeIcon:
          timelineCommentSettingData.boardAuthorBadgeIcon ??
          defaultTimelineCommentSetting.boardAuthorBadgeIcon,
      });
    }
    setTimelineCommentSettingLoaded(true);
  }, [timelineCommentSettingData]);

  const userRepPath =
    currentUserData?.representativePhotocardPath &&
      currentUserData?.representativePhotocardId
      ? `${currentUserData.representativePhotocardPath}/${currentUserData.representativePhotocardId}`
      : null;
  const homeRepPath = homeSet?.representativePhotocard
    ? `${homeSet.representativePhotocard.collectionPath}/${homeSet.representativePhotocard?.id}`
    : null;
  // 로그인한 유저의 데이터가 로드된 경우 유저 설정만 사용 (홈 기본값 fallback 없음)
  // 비로그인 상태에서만 홈 기본 대표 캐릭터를 표시
  const mainCharCollectionPath =
    currentUserId && currentUserData !== undefined
      ? userRepPath
      : userRepPath || homeRepPath;

  const { data: mainChar } = useDocument<IPhotoCard>(mainCharCollectionPath, {
    listen: true,
  });

  useEffect(() => {
    if (mainChar) {
      setMainPhotocardData(mainChar);
    } else {
      console.log('대표 캐릭터를 찾을 수 없습니다.');
      setMainPhotocardData(null);
    }
  }, [mainChar]);

  // 📍 대표 폼 데이터 로드 (대표 포토카드가 없을 때 사용)
  const representativeFormPath = currentUserData?.representativeFormPath;
  const representativeFormId = currentUserData?.representativeFormId;

  const formDocPath =
    representativeFormPath && representativeFormId
      ? `${representativeFormPath}/${representativeFormId}`
      : null;

  const { data: representativeForm } = useDocument<FormResponse>(formDocPath, {
    listen: true,
  });

  const { data: formConfig } = useDocument<FormConfig>(
    representativeForm?.formConfigId
      ? `formSetting/${representativeForm.formConfigId}`
      : null,
    { listen: true },
  );

  // 대표 폼 데이터 atom 설정
  const representativeFormValue = useMemo<IRepresentativeForm | null>(() => {
    if (!representativeForm || !formConfig) {
      return null;
    }

    // listDisplayMapping에서 제목/썸네일 추출
    const mapping = formConfig.listDisplayMapping;
    const responses = representativeForm.responses;

    let title = representativeForm.title || '';
    let thumbnail = representativeForm.thumbnailUrl || '';
    let subTitle = representativeForm.subTitle || '';

    if (mapping && responses) {
      if (mapping.titleFieldId && responses[mapping.titleFieldId]) {
        title = responses[mapping.titleFieldId].value || title;
      }
      if (mapping.thumbnailFieldId && responses[mapping.thumbnailFieldId]) {
        const thumbValue = responses[mapping.thumbnailFieldId].value;
        thumbnail = Array.isArray(thumbValue)
          ? thumbValue[0]
          : thumbValue || thumbnail;
      }
      if (mapping.subTitleFieldId && responses[mapping.subTitleFieldId]) {
        subTitle = responses[mapping.subTitleFieldId].value || subTitle;
      }
    }

    return {
      id: representativeForm.id,
      title,
      subTitle,
      thumbnail,
      camp: representativeForm.camp,
      formConfigId: representativeForm.formConfigId,
      formConfigTitle: formConfig.title,
      authorUid: representativeForm.authorUid,
      formPath: representativeFormPath,
    };
  }, [representativeForm, formConfig, representativeFormPath]);

  // 📍 대표 폼 데이터가 변경될 때만 atom 업데이트
  useEffect(() => {
    // 디버깅 로그
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 [대표폼 디버깅]', {
        representativeFormPath,
        representativeFormId,
        formDocPath,
        representativeForm: representativeForm
          ? {
            id: representativeForm.id,
            title: representativeForm.title,
            formConfigId: representativeForm.formConfigId,
          }
          : null,
        formConfig: formConfig
          ? {
            id: formConfig.id,
            title: formConfig.title,
          }
          : null,
        representativeFormValue,
      });
    }
    setRepresentativeFormData(representativeFormValue);
  }, [representativeFormValue]);

  const { data: gatePageSet } = useDocument<IgatePageSet>(
    `${CollectionPath.MAINHOME}/${ImainhomeDoc.GATEPAGE}`,
    { listen: false },
  );

  const { data: bgEffSet } = useDocument<IbackgroundEffect>(
    SettingCollectionPath.BACKGROUNDEFFECT,
    { listen: true },
  );
  const { data: mainHomeData } = useDocument<ImainHome>(
    `${CollectionPath.MAINHOME}/${ImainhomeDoc.MAINHOME}`,
    { listen: false },
  );

  useEffect(() => {
    if (mainHomeData) {
      setMainHomeSetting(mainHomeData);
    }
  }, [mainHomeData]);

  const prevBgEffRef = useRef<string>('');
  useEffect(() => {
    if (!bgEffSet) return;

    const nextShooting = {
      color: bgEffSet.shootingStar?.color ?? '#fff',
      isActive: masterSet.shootingStar
        ? bgEffSet.shootingStar?.isActive ?? false
        : false,
    };

    // 값이 실제로 변경된 경우에만 Recoil 상태 업데이트 (불필요한 리렌더 방지)
    const nextKey = JSON.stringify({
      isActive: bgEffSet.isActive,
      options: bgEffSet.options,
      shootingStar: nextShooting,
    });
    if (prevBgEffRef.current === nextKey) return;
    prevBgEffRef.current = nextKey;

    setBgEffSet({
      ...bgEffSet,
      shootingStar: nextShooting,
    });
    if (bgEffSet.options) {
      setParticleEffectOptions(bgEffSet.options);
    }
  }, [bgEffSet, setBgEffSet, setParticleEffectOptions]);

  // 기본 재화 설정
  const setDefaultCurrency = useSetRecoilState(defaultCurrencyState);
  const { data: defaultCurrencyData } = useDocument<IstoreCommon>(
    `${CollectionPath.NPC}/storeSetting`,
    { listen: false },
  );
  useEffect(() => {
    if (defaultCurrencyData) {
      setDefaultCurrency({
        moneyUnit: defaultCurrencyData.moneyUnit,
        moneyImgURL: defaultCurrencyData.moneyImgURL,
        logoImgURL: defaultCurrencyData.logoImgURL,
      });
    }
  }, [defaultCurrencyData]);

  useLayoutEffect(() => {
    // 최초 보드 카테고리 세팅
    // initialSet();
    // setInitialHomepage();
    //  -------------------------------------------
    // setInitialBoard();
    // setInitialBackgroundEffect();
    // 보드 추가 -----------------------------------------
    // addBoardSet({ typeName: BOARD_TYPE.CALENDAR, path: 'calendar' });
    // addBoardSet({ typeName: BOARD_TYPE.GALLERY, path: 'gallery' });
    // addBoardSet({ typeName: BOARD_TYPE.TRPG, path: 'trpg' });
    // addBoardSet({ typeName: BOARD_TYPE.MEMO, path: 'memo' });
    // addBoardSet({ typeName: BOARD_TYPE.POSTING, path: 'posting2' });
    // addBoardSet({ typeName: BOARD_TYPE.PHOTOCARD, path: 'photocard' });
    // addBoardSet({ typeName: BOARD_TYPE.GUESTBOOK, path: 'guestbook2' });
    // addBoardSet({ typeName: BOARD_TYPE.TIMELINE, path: 'timeline2' });
    // addBoardSet({ typeName: BOARD_TYPE.COUPLE, path: 'couple' });
    // addBoardSet({ typeName: BOARD_TYPE.HISTORY, path: 'history' });
    // addBoardSet({ typeName: BOARD_TYPE.WORLD, path: 'world' });
    // addBoardSet({ typeName: BOARD_TYPE.STICKERBOARD, path: 'stickerboard' });
    // addBoardSet({ typeName: BOARD_TYPE.EXPLORATION, path: 'exploration' });
    // addBoardSet({ typeName: BOARD_TYPE.RAID, path: 'raid' });
    // addBoardSet({ typeName: BOARD_TYPE.INTERACTIVE_STORY, path:"interactiveStory" });
    // addBoardSet({ typeName: BOARD_TYPE.BETTING, path: 'betting' });
    // addBoardSet({ typeName: BOARD_TYPE.CALCULATE, path: 'calculate' });
    // addBoardSet({ typeName: BOARD_TYPE.FORM, path: "form" });
    // addBoardSet({ typeName: BOARD_TYPE.STORE, path: 'store' });
    // addBoardSet({ typeName: BOARD_TYPE.THREADS, path: "threads" });
    // addBoardSet({ typeName: BOARD_TYPE.QUEST, path: 'quest' });
    // addBoardSet({ typeName: BOARD_TYPE.WORLDMAP, path: 'worldmap' });
    // //  -------------------------------------------
    // 테마 꾸미기 업데이트 + album date정렬 (2.0.8이후)
    // rebaseBoardsTheme();
    // rebaseAlbum();
    //  -------------------------------------------
    // 해시태그 숫자 정정, 타임라인 number id list 정정 F
    // resetHashtagDocIdList({ collectionPath: 'timeline' });
    // resetIdList('memo');
    // 게시판의 docidlist 재정렬
    // resetDocIdList({ collectionPath: 'form' });
    // 검색 작동하지 않을때, 게시판의 모든 게시글을 다시 재수정
    // refetchAllDoc({ collectionPath: 'posting' });
    // refetchAllDoc({ collectionPath: 'guestbook' });
    // refetchAllDoc({ collectionPath: 'timeline' });
    // refetchAllDoc({ collectionPath: 'memo' });
    // refetchAllDoc({ collectionPath: 'gallery' });
    // refetchAllDoc({ collectionPath: 'trpg' });
    // 콜렉션 전체 옮기기
    // moveAllDoc({ collectionPath: 'trpg' });
    // 시리즈 목록 재정렬
    // refetchSeries({ collectionPath: 'posting' });
    // 모든 이모티콘에 order 추가
    // resetAllEmoticonOrder();
    // 타임라인 게시판 numberId 일괄 재적용
    // refetchDocNumberID({ collectionPath: 'guestbook' });
    // 페이지 설정 마이그레이션
    // migratePageSetting();
  }, []);

  useEffect(() => {
    if (homeSet && homeSet.exists) {
      mainSetting(homeSet);
      setLogoType(homeSet.homepageSet.logoType);
      setLayoutType(homeSet.layoutType);
      setInitialLayoutType(homeSet.layoutType);
      setLayoutDir(homeSet.layoutDir || LAYOUT_DIR.LEFT);
      setMenuTypeAtom(homeSet.menu.menuType || MENU_DESIGN_TYPE.COLOR);
      setMenuFixed(homeSet.menu.menuFixed || true);
      setMenuHeight(formatPixelValue(homeSet.menu.menuHeight, '80px'));
      setMenuWidth(formatPixelValue(homeSet.menu.menuWidth, '220px'));
      setLayoutWidth(formatPixelValue(homeSet.menu.layoutWidth, '1280px'));
      setShowAside(homeSet.menu.showSide ?? false);
      setShowBlogSlide(homeSet.menu.showBlogSlide ?? false);
      setIconDirection(homeSet.menu.iconDirection ?? 'column');
      setTopNavSideSeparated(homeSet.menu.topNavSideSeparated ?? true);
    }
  }, [homeSet]);

  const [isGatePage, setIsGatePage] = useState(false);
  const charPanelLayoutOverride = useRecoilValue(charPanelLayoutOverrideState);

  useLayoutEffect(() => {
    if (homeSet) setLoading(true);
  }, [homeSet]);

  useEffect(() => {
    let isGate = false;
    if (masterSet.gatePage && gatePageSet && gatePageSet.isActive) {
      if (gatePageSet.setLogin) {
        // 로그인 필요 게이트: localStorage 캐시 무시, 인증 여부만 체크
        // (탈퇴/로그아웃 후에도 게이트가 다시 표시되어야 함)
        isGate = !currentUserId;
      } else {
        // 진입 버튼 게이트: 방문 여부(localStorage 캐시) + 비로그인 상태 체크
        isGate = Boolean(gatePageActive && !currentUserId);
      }
    }
    if (masterSet.gatePage) setIsGatePage(isGate);
  }, [gatePageSet, currentUserId, gatePageActive]);

  const isShowRandomNeighbor =
    (mainHomeData && mainHomeData.showRandomNeighbor) || false;

  useEffect(() => {
    const isPrevOpen = prevFloatingOpenRef.current.musicPlayer;
    if (isMusicPlayerOpen && !isPrevOpen) {
      bringFloatingToFront('musicPlayer');
    }
    prevFloatingOpenRef.current.musicPlayer = isMusicPlayerOpen;
  }, [isMusicPlayerOpen, bringFloatingToFront]);

  useEffect(() => {
    const isPrevOpen = prevFloatingOpenRef.current.notification;
    if (isNotificationOpen && !isPrevOpen) {
      bringFloatingToFront('notification');
    }
    prevFloatingOpenRef.current.notification = isNotificationOpen;
  }, [isNotificationOpen, bringFloatingToFront]);

  useEffect(() => {
    const isPrevOpen = prevFloatingOpenRef.current.chat;
    if (isChatOpen && !isPrevOpen) {
      bringFloatingToFront('chat');
    }
    prevFloatingOpenRef.current.chat = isChatOpen;
  }, [isChatOpen, bringFloatingToFront]);

  return (
    <>
      <Simeji />
      {isShowRandomNeighbor && <NeighborhoodBtn />}
      {/* 배경 */}
      <BackgroundEffect />
      <WebglBackground homeSet={homeSet || null} />
      <Suspense fallback={null}>
        <BackgroundDecorations homeSet={homeSet || null} />
      </Suspense>
      <MouseEffect />
      {/* 음악 이펙트 */}
      <MusicPlayerDraggable
        zIndex={floatingLayers.musicPlayer}
        onActivate={() => bringFloatingToFront('musicPlayer')}
      />
      {/* 클릭 이펙트 시스템 */}
      <ClickEffectsManager />

      {/* notification관련 */}
      <NotificationPermissionManager />
      <NotificationDraggable
        zIndex={floatingLayers.notification}
        onActivate={() => bringFloatingToFront('notification')}
      />
      <ChatMessageNotification />
      <ReplyModal />
      {openBrowserAlert && (
        <BrowserAlertModal close={closeModal} browserName={browserName} />
      )}

      {/* 페이지 레이아웃 */}
      {isGatePage ? <GatePage /> : <LayoutWrapper homeSet={homeSet} />}
      {/* <LayoutWrapper homeSet={homeSet} /> */}

      {/* 커뮤니티 - 캐릭터 판넬
          cummuMode.isActive이거나, 특정 페이지가 override atom을 설정한 경우 표시 */}
      {(homeSet?.cummuMode?.isActive || !!charPanelLayoutOverride) &&
        !isGatePage && <CharacterPanel />}
      {masterSet.petSystem && <PetDraggable />}

      {isFloating && <FloatingPet onClose={() => setIsFloating(false)} />}

      {/* 플로팅 채팅 */}
      <Suspense fallback={null}>
        <ChatFloating
          zIndex={floatingLayers.chat}
          onActivate={() => bringFloatingToFront('chat')}
        />
      </Suspense>

      {/* 버전 업데이트 모달 (전역 관리) */}
      <Suspense fallback={null}>
        <VersionUpdateModal />
      </Suspense>

      {/* 전역 스티커 표시 */}
      <Suspense fallback={null}>
        <GlobalStickerDisplay />
      </Suspense>

      {/* 레이드 알림 스낵바 */}
      <Suspense fallback={null}>
        <RaidNotificationSnackbar />
      </Suspense>

      {/* 전역 레이드 모달 */}
      <Suspense fallback={null}>
        <GlobalRaidModal />
      </Suspense>

      {/* PWA 설치 유도 배너 */}
      <Suspense fallback={null}>
        <PWAInstallBanner />
      </Suspense>

      {/* 공지 팝업 */}
      <Suspense fallback={null}>
        <AnnouncementPopup />
      </Suspense>

      <AlertModal />

      {openRegisterModal && <RegisterModal />}
    </>
  );
}

export default Main;
